import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  Phone, 
  Mail, 
  Globe, 
  MapPin,
  Edit2,
  Trash2,
  Briefcase,
  MessageSquare,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface Account {
  id: string;
  accountId: string;
  accountName: string;
  accountNameKana: string;
  tradeStatus: string;
  postalCode: string;
  prefecture: string;
  city: string;
  street: string;
  building: string;
  telMain: string;
  faxMain: string;
  website: string;
  industryType: string;
  employeeCount: string;
  capital: string;
  fiscalMonth: number;
  remarks: string;
  createdAt: string;
  updatedAt: string;
}

interface Deal {
  id: string;
  dealName: string;
  dealStatus: string;
  dealProfit: number;
}

interface Activity {
  id: string;
  activityTitle: string;
  activityDatetime: string;
}

export function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAccount();
  }, [id]);

  const fetchAccount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('企業情報の取得に失敗しました');
      }
      
      const data = await response.json();
      setAccount(data.account);
      setDeals(data.deals || []);
      setActivities(data.activities || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('この企業を削除しますか？関連する案件・商談も削除されます。')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('削除に失敗しました');
      }
      
      navigate('/accounts');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      '取引中': 'bg-green-100 text-green-700',
      '休眠': 'bg-gray-100 text-gray-700',
      '見込み': 'bg-blue-100 text-blue-700',
      '失注': 'bg-red-100 text-red-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-red-500 mb-4">{error || '企業が見つかりません'}</p>
        <Button onClick={() => navigate('/accounts')}>企業一覧に戻る</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounts')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {account.accountName}
              </h1>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(account.tradeStatus)}`}>
                {account.tradeStatus || '未設定'}
              </span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {account.accountId}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            leftIcon={<Edit2 size={16} />}
            onClick={() => navigate(`/accounts/${id}/edit`)}
          >
            編集
          </Button>
          <Button 
            variant="danger" 
            leftIcon={<Trash2 size={16} />}
            onClick={handleDelete}
          >
            削除
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 基本情報 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 size={20} />
              基本情報
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>企業名（かな）</p>
                <p style={{ color: 'var(--text-primary)' }}>{account.accountNameKana || '-'}</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>業種</p>
                <p style={{ color: 'var(--text-primary)' }}>{account.industryType || '-'}</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>従業員数</p>
                <p style={{ color: 'var(--text-primary)' }}>{account.employeeCount || '-'}</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>資本金</p>
                <p style={{ color: 'var(--text-primary)' }}>{account.capital || '-'}</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>決算月</p>
                <p style={{ color: 'var(--text-primary)' }}>{account.fiscalMonth ? `${account.fiscalMonth}月` : '-'}</p>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            <div>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>所在地</p>
              <div className="flex items-start gap-2">
                <MapPin size={18} style={{ color: 'var(--text-secondary)' }} />
                <p style={{ color: 'var(--text-primary)' }}>
                  〒{account.postalCode || '-'}<br />
                  {account.prefecture}{account.city}{account.street} {account.building}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Phone size={18} style={{ color: 'var(--text-secondary)' }} />
                <p style={{ color: 'var(--text-primary)' }}>{account.telMain || '-'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={18} style={{ color: 'var(--text-secondary)' }} />
                <p style={{ color: 'var(--text-primary)' }}>{account.faxMain || '-'}</p>
              </div>
              {account.website && (
                <div className="flex items-center gap-2 md:col-span-2">
                  <Globe size={18} style={{ color: 'var(--text-secondary)' }} />
                  <a 
                    href={account.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {account.website}
                  </a>
                </div>
              )}
            </div>

            {account.remarks && (
              <>
                <hr style={{ borderColor: 'var(--border-color)' }} />
                <div>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>備考</p>
                  <p style={{ color: 'var(--text-primary)' }} className="whitespace-pre-wrap">
                    {account.remarks}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* サイドバー */}
        <div className="space-y-6">
          {/* 関連案件 */}
          <Card padding="none">
            <CardHeader className="flex flex-row items-center justify-between p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Briefcase size={18} />
                関連案件
              </CardTitle>
              <Link 
                to={`/deals/new?accountId=${id}`}
                className="text-blue-600 hover:text-blue-700"
              >
                <Plus size={18} />
              </Link>
            </CardHeader>
            <CardContent>
              {deals.length === 0 ? (
                <p className="text-center py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  案件がありません
                </p>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {deals.map((deal) => (
                    <Link 
                      key={deal.id} 
                      to={`/deals/${deal.id}`}
                      className="block py-3 hover:bg-gray-50 -mx-4 px-4"
                    >
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {deal.dealName}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {deal.dealStatus} • ¥{deal.dealProfit?.toLocaleString() || 0}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 最近の商談 */}
          <Card padding="none">
            <CardHeader className="flex flex-row items-center justify-between p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare size={18} />
                最近の商談
              </CardTitle>
              <Link 
                to={`/activities/new?accountId=${id}`}
                className="text-blue-600 hover:text-blue-700"
              >
                <Plus size={18} />
              </Link>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-center py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  商談がありません
                </p>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {activities.map((activity) => (
                    <Link 
                      key={activity.id} 
                      to={`/activities/${activity.id}`}
                      className="block py-3 hover:bg-gray-50 -mx-4 px-4"
                    >
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.activityTitle || '(タイトルなし)'}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {activity.activityDatetime 
                          ? new Date(activity.activityDatetime).toLocaleDateString('ja-JP')
                          : '-'
                        }
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
